import { formatDate, useGet } from '@threatadvisor/react-utils';
import {
  DetectionCaseViewModel,
  ResponsiveActionViewModel,
  ServiceCaseViewModel,
  SupportCaseViewModel
} from '@threatadvisor/react-utils/src/types/generated.SecurityEvent';
import { Table } from 'antd';
import React, { ReactElement } from 'react';

export type MdrCasesProps = {
  securityEventApi: string;
  clientUid: string;
  reportStart: string;
  reportEnd: string;
  showClosed?: boolean;
};

export default function MdrCases(props: MdrCasesProps): ReactElement {
  const { securityEventApi, clientUid, reportStart, reportEnd, showClosed } = props;

  const { data: openDetections } = useGet<DetectionCaseViewModel[]>(
    `${securityEventApi}/report/detectioncases/active`,
    {
      query: {
        clientUid: clientUid
      }
    }
  );
  const { data: closedDetections } = useGet<DetectionCaseViewModel[]>(
    `${securityEventApi}/report/detectioncases/closed`,
    {
      query: {
        clientUid: clientUid,
        startDate: reportStart,
        endDate: reportEnd
      },
      loadIfDefined: showClosed
    }
  );

  const { data: openServices } = useGet<ServiceCaseViewModel[]>(
    `${securityEventApi}/report/servicecases/active`,
    {
      query: {
        clientUid: clientUid
      }
    }
  );
  const { data: closedServices } = useGet<ServiceCaseViewModel[]>(
    `${securityEventApi}/report/servicecases/closed`,
    {
      query: {
        clientUid: clientUid,
        startDate: reportStart,
        endDate: reportEnd
      },
      loadIfDefined: showClosed
    }
  );

  const { data: openSupports } = useGet<SupportCaseViewModel[]>(
    `${securityEventApi}/report/supportcases/active`,
    {
      query: {
        clientUid: clientUid
      }
    }
  );
  const { data: closedSupports } = useGet<SupportCaseViewModel[]>(
    `${securityEventApi}/report/supportcases/closed`,
    {
      query: {
        clientUid: clientUid,
        startDate: reportStart,
        endDate: reportEnd
      },
      loadIfDefined: showClosed
    }
  );
  const { data: responsiveActions } = useGet<ResponsiveActionViewModel[]>(
    `${securityEventApi}/report/responsiveactions`,
    {
      query: {
        clientUid: clientUid,
        startDate: reportStart,
        endDate: reportEnd
      },
      loadIfDefined: clientUid
    }
  );
  return (
    <>
      <h2>Cases for Review</h2>

      <h3>Active Detection Cases</h3>
      <Table
        columns={[
          {
            key: 'createdDate',
            dataIndex: 'createdDate',
            title: 'Opened',
            render: text => <div style={{ width: 75 }}>{formatDate(new Date(text))}</div> //formatDate(text)
          },
          {
            key: 'title',
            dataIndex: 'title',
            title: 'Case',
            render: (text, record) => {
              return (
                <div style={{ width: 600 }}>{`TA-DT-${record.id} | ${record.priority} | ${
                  record.secEvents && record.secEvents.length > 0
                    ? record.secEvents[0].source + ' | '
                    : ''
                } | ${record.title}`}</div>
              );
            }
          }
        ]}
        size="small"
        rowKey={record => record.uid}
        dataSource={openDetections
          ?.sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
          .filter(dc => dc.priority !== 'P5')}
        pagination={false}
      />
      {showClosed ? (
        <>
          <h3>Closed & Resolved Detection Cases</h3>
          <Table
            columns={[
              {
                key: 'createdDate',
                dataIndex: 'createdDate',
                title: 'Date',
                render: text => formatDate(new Date(text)) //formatDate(text)
              },
              {
                key: 'title',
                dataIndex: 'title',
                title: 'Case',
                render: (text, record) => {
                  return `TA-DT-${record.id} | ${record.priority} | ${
                    record.secEvents && record.secEvents.length > 0
                      ? record.secEvents[0].source + ' | '
                      : ''
                  } | ${record.title}`;
                }
              }
            ]}
            size="small"
            rowKey={record => record.uid}
            dataSource={closedDetections
              ?.sort(
                (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
              )
              .filter(dc => dc.priority !== 'P5')}
            pagination={false}
          />
        </>
      ) : null}
      {responsiveActions && responsiveActions.length > 0 ? (
        <>
          <h3>Responsive Actions</h3>
          <Table
            dataSource={responsiveActions}
            pagination={false}
            footer={() => (
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <span>{`Total Responsive Actions: ${responsiveActions.length}`}</span>
              </div>
            )}
            columns={[
              {
                key: 'createdDate',
                dataIndex: 'createdDate',
                title: 'Created',
                render: text => <div style={{ width: 75 }}>{formatDate(new Date(text))}</div> //formatDate(text)
              },
              {
                key: 'case',
                title: 'case',
                render: (_, record) => `TA-DT-${record.detectionCase?.id}`
              },
              { key: 'action', title: 'action', render: (_, record) => record.responseType?.label },
              { key: 'target', title: 'target', render: (_, record) => record.target }
            ]}
          />
        </>
      ) : null}
      <h3>Active Service Cases</h3>
      <Table
        columns={[
          {
            key: 'createdDate',
            dataIndex: 'createdDate',
            title: 'Opened',
            render: text => <div style={{ width: 75 }}>{formatDate(new Date(text))}</div> //formatDate(text)
          },
          {
            key: 'title',
            dataIndex: 'title',
            title: 'Case',
            render: (text, record) => {
              return (
                <div
                  style={{
                    width: 600
                  }}
                >{`TA-SV-${record.id} | ${record.priority} | ${record.title}`}</div>
              );
            }
          }
        ]}
        size="small"
        rowKey={record => record.uid}
        dataSource={openServices
          ?.sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
          .filter(dc => dc.priority !== 'P5')}
        pagination={false}
      />
      {showClosed ? (
        <>
          <h3>Closed & Resolved Service Cases</h3>
          <Table
            columns={[
              {
                key: 'createdDate',
                dataIndex: 'createdDate',
                title: 'Date',
                render: text => formatDate(new Date(text)) //formatDate(text)
              },
              {
                key: 'title',
                dataIndex: 'title',
                title: 'Case',
                render: (text, record) => {
                  return `TA-SV-${record.id} | ${record.priority} | ${record.title}`;
                }
              }
            ]}
            size="small"
            rowKey={record => record.uid}
            dataSource={closedServices
              ?.sort(
                (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
              )
              .filter(dc => dc.priority !== 'P5')}
            pagination={false}
          />
        </>
      ) : null}

      <h3>Active Support Cases</h3>
      <Table
        columns={[
          {
            key: 'createdDate',
            dataIndex: 'createdDate',
            title: 'Opened',
            render: text => <div style={{ width: 75 }}>{formatDate(new Date(text))}</div> //formatDate(text)
          },
          {
            key: 'title',
            dataIndex: 'title',
            title: 'Case',
            render: (text, record) => {
              return (
                <div
                  style={{
                    width: 600
                  }}
                >{`TA-SP-${record.id} | ${record.priority} | ${record.title}`}</div>
              );
            }
          }
        ]}
        size="small"
        rowKey={record => record.uid}
        dataSource={openSupports
          ?.sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
          .filter(dc => dc.priority !== 'P5')}
        pagination={false}
      />
      {showClosed ? (
        <>
          <h3>Closed & Resolved Support Cases</h3>
          <Table
            columns={[
              {
                key: 'createdDate',
                dataIndex: 'createdDate',
                title: 'Date',
                render: text => formatDate(new Date(text)) //formatDate(text)
              },
              {
                key: 'title',
                dataIndex: 'title',
                title: 'Case',
                render: (text, record) => {
                  return `TA-SP-${record.id} | ${record.priority} | ${record.title}`;
                }
              }
            ]}
            size="small"
            rowKey={record => record.uid}
            dataSource={closedSupports
              ?.sort(
                (a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime()
              )
              .filter(dc => dc.priority !== 'P5')}
            pagination={false}
          />
        </>
      ) : null}
    </>
  );
}
